import { Typography, Box } from "@mui/material";
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { useDirectorySearchContext } from "../contexts/DirectorySearchContext";
import { useMemo } from "react";
import { DirectoryPagination } from "./DirectoryPagination";
import { DisplayParams } from "../types/classes/DisplayParams";
import Item from "./Item";
import { GetResultsByPage } from "../../global/utilities/PaginationUtilities";
import { DirectoryResource } from '../constants/DirectoryResource'

interface ItemCollectionDisplayProps {
    displayParams: DisplayParams;
    onDisplayChange: (displayParams: DisplayParams) => void;
}

/**
 * SummaryDisplay Component
 * 
 * This component creates the display for the Fmember directory results. It contains the result header labels and result items.
 * Pagination is implemented here.
 * 
 * Props:
 * - displayParams: An object of type DisplayParams containing the current display parameters.
 * - onDisplayChange: A function to handle the display change event.
 * 
 * Example usage:
 * ```tsx
 * <ItemCollectionDisplay displayParams={displayParams} onDisplayChange={handleDisplayChange} />
 * ```
 * 
 * @param {DisplayParams} displayParams - The current display parameters.
 * @param {(displayParams: DisplayParams) => void} onDisplayChange - The function to handle the display change event.
 * @returns {JSX.Element} The component displaying the directory results with pagination.
 */
const ItemCollectionDisplay: React.FC<ItemCollectionDisplayProps> = ({ displayParams, onDisplayChange }) => {
    const resultsPerPage: number = Number(process.env.REACT_APP_RESULTS_PER_PAGE) || 10; // Default to 10 if not set in environment variables
    const { loadingFilteredList, filteredMemberList } = useDirectorySearchContext();
    const resultsForPage = useMemo(() =>
        GetResultsByPage(filteredMemberList, displayParams.Page),
        [filteredMemberList, displayParams.Page]

    );

    const pageCount: number = Math.ceil(filteredMemberList.length / resultsPerPage);

    return (
        <Box paddingLeft={3} sx={{ width: "100%" }}>
            {loadingFilteredList && (
                <Box alignItems="center" sx={{ my: 2 }}>
                    <CircularProgress />
                </Box>
            )}
            {<>
                <Grid container sx={{ width: "100%" }} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                    <Grid item xs={12} sm={6} md={6}>
                        <Typography variant="h5" sx={{ ml: 2 }}>{DirectoryResource.pediatrician_lbl}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="h5" sx={{ ml: 0 }}>{DirectoryResource.city_lbl}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="h5" sx={{ ml: -1 }}>{DirectoryResource.state_lbl}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="h5" sx={{ ml: -1 }}>{DirectoryResource.phone_lbl}</Typography>
                    </Grid>
                </Grid>
                {resultsForPage.map((item, index) =>
                (<Item member={item}
                    index={index}
                    key={`member-summary-${index}`}
                    displayParams={displayParams}
                    onDisplayChange={onDisplayChange} />))}
                <Box paddingTop={3}>
                    {(filteredMemberList.length === 0 && !displayParams.IsReset) && <Typography>{DirectoryResource.no_results_lbl}</Typography>}
                    {filteredMemberList.length > 0 &&
                        <DirectoryPagination
                            pageCount={pageCount}
                            displayParams={displayParams}
                            onPageChange={onDisplayChange} />
                    }
                </Box>
            </>}
        </Box >
    );
};

export default ItemCollectionDisplay;